import { stripArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import {
  classifyLlmField,
  type LlmFieldComparison,
} from 'features/libra/llmDiff/classifyLlmField'
import { formatFieldValue } from 'features/libra/llmDiff/formatFieldValue'
import { flattenTicketFieldValues } from 'features/libra/ontology/flattenOntologyFields'
import {
  type FieldValue,
  TicketFieldValuesSchema,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'

/**
 * Pure reducer that turns a collection of libra-ticket rolodex entries into
 * LLM-usage stats. Lives in its own module (no React, no memoisation) so the
 * dashboard UI and any future background analytics can reuse the exact same
 * accept/edit/reject definitions without drifting.
 *
 * The classifier is pre-shared via `classifyLlmField`, and the value
 * normalisations (`stripArrayRowIds`, `formatFieldValue`) mirror what the
 * diff drawer already does — otherwise array-of-object fields would always
 * look `edited` because of locally stamped `_id` tokens.
 */

/**
 * Outcomes shown in the stats view.
 *
 * `'bothMissing'` surfaces fields where the LLM ran but produced nothing
 * meaningful (e.g. `llmValue: null`/`''`/`[]`) and the user also has no
 * value. These rows are visible in the per-ticket expansion so reviewers
 * can see the LLM was invoked and came back empty, but they are
 * intentionally excluded from every numeric metric (per-ticket counts,
 * per-field aggregation, summary-card totals) because counting them as
 * `rejected` would inflate "things the user threw away" when there was
 * nothing to throw away.
 *
 * The degenerate `'userOnly'` case (LLM value reduces to the missing
 * placeholder while the user has a value) is also routed here, for the
 * same reason — the LLM didn't make a real suggestion to accept.
 */
export type NormalizedLlmFieldComparison =
  | 'accepted'
  | 'edited'
  | 'rejected'
  | 'bothMissing'

export interface FieldComparisonDetail {
  fieldId: string
  fieldName: string
  comparison: NormalizedLlmFieldComparison
  userText: string
  llmText: string
}

export interface TicketLlmStats {
  ticketId: string
  ticketTitle: string
  site: string | undefined
  hasLlmLineage: boolean
  /** Count of fields where llmValue !== undefined AND comparison === 'accepted' */
  accepted: number
  edited: number
  rejected: number
  /** Total fields with llmValue (accepted + edited + rejected) */
  totalFieldsWithLlm: number
  fields: FieldComparisonDetail[]
}

export interface FieldLlmStats {
  fieldId: string
  fieldName: string
  accepted: number
  edited: number
  rejected: number
  /** Tickets where this field has llmValue */
  total: number
}

export interface TicketLlmStatsResult {
  /** All tickets considered (after site filter) */
  totalTickets: number
  ticketsWithLlm: number
  ticketsWithoutLlm: number
  fieldTotals: { accepted: number; edited: number; rejected: number }
  perTicket: TicketLlmStats[]
  perField: FieldLlmStats[]
}

export interface ComputeTicketLlmStatsOptions {
  /** Filter to only tickets whose `tags.site` equals this value. Leave undefined (or empty string) for "all sites". */
  siteFilter?: string
}

export function computeTicketLlmStats(
  tickets: ReadonlyArray<LibraTicket>,
  fieldIdToName: ReadonlyMap<string, string>,
  options?: ComputeTicketLlmStatsOptions
): TicketLlmStatsResult {
  const siteFilter =
    options?.siteFilter && options.siteFilter.length > 0
      ? options.siteFilter
      : undefined

  // Own the working array so the in-place sort below is safe; `.filter()`
  // already allocates, so only the pass-through path needs `.slice()`.
  const scoped = siteFilter
    ? tickets.filter((ticket) => ticket.tags.site === siteFilter)
    : tickets.slice()

  // Sort the tickets first rather than the derived stats, because the stats
  // shape intentionally omits `updatedAt` (it's a table-row concern, not a
  // count concern). Most-recent first, with `id` as a stable tie-breaker.
  scoped.sort(compareTickets)

  const perTicket: TicketLlmStats[] = scoped.map((ticket) =>
    buildTicketStats(ticket, fieldIdToName)
  )

  // Fold both aggregate passes into a single traversal — the list is small
  // in practice (one campaign's tickets) but there's no reason to walk it
  // twice when every reduction reads the same shape.
  let ticketsWithLlm = 0
  const fieldTotals = { accepted: 0, edited: 0, rejected: 0 }
  for (const t of perTicket) {
    if (t.hasLlmLineage) ticketsWithLlm += 1
    fieldTotals.accepted += t.accepted
    fieldTotals.edited += t.edited
    fieldTotals.rejected += t.rejected
  }
  const ticketsWithoutLlm = perTicket.length - ticketsWithLlm

  const perField = buildPerField(perTicket, fieldIdToName)

  return {
    totalTickets: scoped.length,
    ticketsWithLlm,
    ticketsWithoutLlm,
    fieldTotals,
    perTicket,
    perField,
  }
}

function emptyTicketStats(ticket: LibraTicket): TicketLlmStats {
  return {
    ticketId: ticket.id,
    ticketTitle: ticket.title.length > 0 ? ticket.title : ticket.id,
    site: ticket.tags.site ?? undefined,
    hasLlmLineage: false,
    accepted: 0,
    edited: 0,
    rejected: 0,
    totalFieldsWithLlm: 0,
    fields: [],
  }
}

function buildTicketStats(
  ticket: LibraTicket,
  fieldIdToName: ReadonlyMap<string, string>
): TicketLlmStats {
  // `LibraTicket.fieldValues` is typed as `FieldValues` but the upstream
  // schema only enforces `Record<string, unknown>` and casts — re-validate
  // so downstream code can trust the `FieldValue` shape per entry.
  // `TicketFieldValuesSchema` validates BOTH scopes: top-level slots and
  // the reserved `components` subtree.
  const parsed = TicketFieldValuesSchema.safeParse(ticket.fieldValues)

  if (!parsed.success) return emptyTicketStats(ticket)

  // Flatten across both scopes so component-scoped llmValues (asset-based
  // FFS tickets) count toward the stats instead of being silently skipped.
  // Display names resolve through `fieldIdToName` (merged across workflows
  // by the caller), with the row's own ontology-derived name as fallback.
  const rows = flattenTicketFieldValues(parsed.data, undefined).filter(
    (row) => row.value.llmValue !== undefined
  )

  if (rows.length === 0) return emptyTicketStats(ticket)

  const fields: FieldComparisonDetail[] = []
  let accepted = 0
  let edited = 0
  let rejected = 0

  for (const row of rows) {
    const { fieldId, value: wrapper } = row

    const userFv: FieldValue = {
      value: stripArrayRowIds(wrapper.value),
      source: wrapper.source,
    }
    const llmFv: FieldValue = {
      value: stripArrayRowIds(wrapper.llmValue),
      source: wrapper.source,
    }
    const userText = formatFieldValue(userFv)
    const llmText = formatFieldValue(llmFv)
    const rawComparison = classifyLlmField({
      userFv,
      llmFv,
      userText,
      llmText,
    })
    const comparison = normaliseComparison(rawComparison)

    switch (comparison) {
      case 'accepted':
        accepted += 1
        break
      case 'edited':
        edited += 1
        break
      case 'rejected':
        rejected += 1
        break
      case 'bothMissing':
        break
    }

    fields.push({
      fieldId,
      fieldName: fieldIdToName.get(fieldId) ?? row.fieldName ?? fieldId,
      comparison,
      userText,
      llmText,
    })
  }

  return {
    ticketId: ticket.id,
    ticketTitle: ticket.title.length > 0 ? ticket.title : ticket.id,
    site: ticket.tags.site ?? undefined,
    hasLlmLineage: true,
    accepted,
    edited,
    rejected,
    totalFieldsWithLlm: accepted + edited + rejected,
    fields,
  }
}

/**
 * Map the 5-outcome classifier onto the 4 outcomes the stats view exposes.
 *
 * `'both-missing'` means the LLM produced no meaningful value and the user
 * also has none — surfaced as `'bothMissing'` so the per-ticket expansion
 * still lists the field, but no numeric metric counts it.
 *
 * `'userOnly'` is near-unreachable because we gate the loop on
 * `llmValue !== undefined`, but if a `llmValue` exists whose
 * stripped-and-formatted text reduces to the missing placeholder, we
 * likewise treat it as `'bothMissing'`: the LLM made no actionable
 * suggestion, so it shouldn't register as an "acceptance".
 */
function normaliseComparison(
  raw: LlmFieldComparison
): NormalizedLlmFieldComparison {
  switch (raw) {
    case 'accepted':
      return 'accepted'
    case 'edited':
      return 'edited'
    case 'rejected':
      return 'rejected'
    case 'both-missing':
      return 'bothMissing'
    case 'userOnly':
      return 'bothMissing'
    default:
      return assertNever(raw)
  }
}

function assertNever(x: never): never {
  throw new Error(
    `normaliseComparison received an unexpected LlmFieldComparison: ${String(x)}`
  )
}

/**
 * Share accept-rate math between the per-ticket and per-field tables so the
 * two views can't drift on edge cases (e.g. zero-denominator handling).
 */
export const ticketAcceptRate = (t: TicketLlmStats): number =>
  t.totalFieldsWithLlm === 0 ? 0 : t.accepted / t.totalFieldsWithLlm

export const fieldAcceptRate = (f: FieldLlmStats): number =>
  f.total === 0 ? 0 : f.accepted / f.total

/**
 * Consistent `—` when there's no denominator to divide by, otherwise a
 * rounded percentage. Callers pass the already-computed rate + total so the
 * function is agnostic to ticket vs field shapes.
 */
export const formatAcceptRate = (rate: number, total: number): string =>
  total === 0 ? '—' : `${Math.round(rate * 100)}%`

/**
 * perTicket order: most-recently-updated first, with `id` as a tie-breaker
 * so tickets with identical `updatedAt` stamps stay in a stable,
 * deterministic order across invocations. Sorting by ISO-8601 strings
 * lexicographically is equivalent to chronological order for the timezone
 * suffixes we emit.
 */
function compareTickets(a: LibraTicket, b: LibraTicket): number {
  const au = a.updatedAt ?? ''
  const bu = b.updatedAt ?? ''
  if (au !== bu) return au < bu ? 1 : -1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

function buildPerField(
  perTicket: ReadonlyArray<TicketLlmStats>,
  fieldIdToName: ReadonlyMap<string, string>
): FieldLlmStats[] {
  const byId = new Map<string, FieldLlmStats>()

  for (const ticket of perTicket) {
    if (!ticket.hasLlmLineage) continue
    for (const detail of ticket.fields) {
      // bothMissing rows are visible in per-ticket breakdowns but must not
      // contribute to any aggregate count — a field whose only appearances
      // are "LLM came back empty" has no signal worth showing on the
      // per-field table.
      if (detail.comparison === 'bothMissing') continue

      let entry = byId.get(detail.fieldId)
      if (!entry) {
        entry = {
          fieldId: detail.fieldId,
          fieldName: fieldIdToName.get(detail.fieldId) ?? detail.fieldId,
          accepted: 0,
          edited: 0,
          rejected: 0,
          total: 0,
        }
        byId.set(detail.fieldId, entry)
      }
      entry[detail.comparison] += 1
      entry.total += 1
    }
  }

  const result = Array.from(byId.values())
  result.sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total
    return a.fieldId < b.fieldId ? -1 : a.fieldId > b.fieldId ? 1 : 0
  })
  return result
}
